import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft,
  Users,
  Send,
  CheckCircle,
  Loader2,
  AlertCircle,
} from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Alert, AlertDescription } from '../components/ui/alert';
import { api } from '../services/api';
import { invitationService } from '../services/invitationService';
import { LoadingSpinner } from '../components/LoadingSpinner';

type CoachRole = 'Co-Coach' | 'Assistant Coach';

// localStorage helper for persisting assistant coach info per team
// Uses a single map stored under one key for reliability
const COACHES_STORAGE_KEY = 'gamepath_assistant_coaches';

interface StoredCoach { firstName: string; lastName: string; role: string }

function getAllStoredCoaches(): Record<string, StoredCoach> {
  try {
    const raw = localStorage.getItem(COACHES_STORAGE_KEY);
    if (!raw) return {};
    return JSON.parse(raw);
  } catch { return {}; }
}

function saveCoachToLocalStorage(teamId: string, first: string, last: string, coachRole: string) {
  const coaches = getAllStoredCoaches();
  coaches[teamId] = { firstName: first, lastName: last, role: coachRole };
  localStorage.setItem(COACHES_STORAGE_KEY, JSON.stringify(coaches));
  // Also write the legacy per-team key so old reads still work
  localStorage.setItem(`assistant_coach_${teamId}`, JSON.stringify(coaches[teamId]));
}

export function getAssistantCoachFromLocalStorage(teamId: string): StoredCoach | null {
  // Try the central map first
  const coaches = getAllStoredCoaches();
  if (coaches[teamId]) return coaches[teamId];

  // Fallback: legacy per-team key
  try {
    const raw = localStorage.getItem(`assistant_coach_${teamId}`);
    if (raw) return JSON.parse(raw);
  } catch { /* ignore */ }

  // Last resort: if there's only one coach stored, assume it belongs to this team
  // (covers the case where the coach was saved under a mismatched ID)
  const entries = Object.values(coaches);
  if (entries.length === 1) return entries[0];

  // Also scan legacy keys
  try {
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith('assistant_coach_') && key !== COACHES_STORAGE_KEY) {
        const raw = localStorage.getItem(key);
        if (raw) return JSON.parse(raw);
      }
    }
  } catch { /* ignore */ }

  return null;
}

interface Team {
  id: string;
  name: string;
  collaboratingCoach?: { id: string; firstName: string; lastName: string; email: string } | null;
  pendingInvitation?: { email: string; firstName: string; lastName: string; expiresAt: string } | null;
}

export const AddCoachPage: React.FC = () => {
  const navigate = useNavigate();
  const { id: urlTeamId } = useParams<{ id: string }>();
  const [teams, setTeams] = useState<Team[]>([]);
  const [selectedTeamId, setSelectedTeamId] = useState(urlTeamId || '');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [role, setRole] = useState<CoachRole | ''>('');
  const [email, setEmail] = useState('');
  const [wantsToInvite, setWantsToInvite] = useState(false);
  const [saving, setSaving] = useState(false);
  const [inviting, setInviting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchTeams = async () => {
      try {
        const res = await api.get('/teams');
        const data: Team[] = res.data.data || [];
        setTeams(data);
        // If team ID came from URL, keep it; otherwise auto-select if only one team
        if (!urlTeamId && data.length === 1) setSelectedTeamId(data[0].id);
      } catch {
        setError('Failed to load teams');
      } finally {
        setLoading(false);
      }
    };
    fetchTeams();
  }, [urlTeamId]);

  const selectedTeam = teams.find((t) => t.id === selectedTeamId);
  const hasExistingCollaborator = !!selectedTeam?.collaboratingCoach;
  const hasPendingInvitation = !!selectedTeam?.pendingInvitation;

  const handleSave = async () => {
    setError('');
    if (!firstName.trim() || !lastName.trim()) {
      setError('First name and last name are required');
      return;
    }
    if (!role) {
      setError('Please select a role');
      return;
    }
    if (!selectedTeamId) {
      setError('Please select a team');
      return;
    }

    if (wantsToInvite) {
      if (!email.trim()) {
        setError('Email is required to send an invitation');
        return;
      }
      if (hasExistingCollaborator) {
        setError('This team already has a collaborating coach. Remove them first in Edit Team Settings.');
        return;
      }
      if (hasPendingInvitation) {
        setError('This team already has a pending invitation. Cancel it first in Edit Team Settings.');
        return;
      }

      setInviting(true);
      try {
        await invitationService.createInvitation({
          teamId: selectedTeamId,
          invitedFirstName: firstName.trim(),
          invitedLastName: lastName.trim(),
          invitedEmail: email.trim(),
        });
        // Save assistant coach info via team update API
        try {
          await api.put(`/teams/${selectedTeamId}`, {
            assistantCoachName: `${firstName.trim()} ${lastName.trim()}`,
            assistantCoachRole: role,
          });
        } catch {
          saveCoachToLocalStorage(selectedTeamId, firstName.trim(), lastName.trim(), role);
        }
        // Complete the task
        try {
          await api.post('/tasks/setup_add_co_coach/complete');
        } catch { /* task may not exist or already completed */ }
        setSuccess(`Invitation sent to ${email.trim()}. They'll receive an email to join your team as a collaborator.`);
      } catch (err: any) {
        setError(err.response?.data?.error?.message || err.message || 'Failed to send invitation');
      } finally {
        setInviting(false);
      }
    } else {
      // Save the coach via team update API
      setSaving(true);
      try {
        await api.put(`/teams/${selectedTeamId}`, {
          assistantCoachName: `${firstName.trim()} ${lastName.trim()}`,
          assistantCoachRole: role,
        });
      } catch {
        // Also save to localStorage as fallback
        saveCoachToLocalStorage(selectedTeamId, firstName.trim(), lastName.trim(), role);
      }
      try {
        await api.post('/tasks/setup_add_co_coach/complete');
      } catch { /* task may not exist or already completed */ }
      setSuccess(`${firstName.trim()} ${lastName.trim()} added as ${role}. You can invite them as a collaborator later from Edit Team Settings.`);
      setSaving(false);
    }
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  if (success) {
    return (
      <div className="p-4 lg:p-8 max-w-lg mx-auto">
        <Card className="shadow-sm border border-gray-200">
          <CardContent className="p-6 text-center">
            <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-6 h-6 text-green-600" />
            </div>
            <h2 className="text-lg font-semibold text-gray-900 mb-2">Done</h2>
            <p className="text-sm text-gray-600 mb-6">{success}</p>
            <Button
              onClick={() => navigate('/dashboard')}
              className="bg-green-600 hover:bg-green-700 text-white min-h-[44px]"
            >
              Back to Dashboard
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-4 lg:p-8 max-w-lg mx-auto">
      <button
        onClick={() => navigate('/dashboard')}
        className="inline-flex items-center gap-1.5 text-sm text-green-700 hover:text-green-800 font-medium mb-4 min-h-[44px]"
      >
        <ArrowLeft className="w-4 h-4" />
        Back to Dashboard
      </button>

      <Card className="shadow-sm border border-gray-200">
        <CardContent className="p-6">
          {/* Header */}
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-full bg-green-50 flex items-center justify-center flex-shrink-0">
              <Users className="w-5 h-5 text-green-700" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Add a Co-Coach or Assistant</h2>
              <p className="text-sm text-gray-500">Name your coaching staff and optionally invite them to collaborate</p>
            </div>
          </div>

          {error && (
            <Alert variant="destructive" className="mb-4">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          <div className="space-y-4">
            {/* Team selector (if multiple teams) */}
            {teams.length > 1 && (
              <div>
                <Label htmlFor="team" className="text-sm font-medium text-gray-700">
                  Team <span className="text-red-500">*</span>
                </Label>
                <select
                  id="team"
                  value={selectedTeamId}
                  onChange={(e) => setSelectedTeamId(e.target.value)}
                  className="mt-1.5 w-full rounded-lg border border-gray-300 bg-white px-3 py-2.5 text-sm min-h-[44px] focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
                >
                  <option value="">Select a team</option>
                  {teams.map((t) => (
                    <option key={t.id} value={t.id}>{t.name}</option>
                  ))}
                </select>
              </div>
            )}

            {/* Name fields */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="coachFirstName" className="text-sm font-medium text-gray-700">
                  First Name <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="coachFirstName"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  placeholder="First name"
                  className="mt-1.5"
                />
              </div>
              <div>
                <Label htmlFor="coachLastName" className="text-sm font-medium text-gray-700">
                  Last Name <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="coachLastName"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  placeholder="Last name"
                  className="mt-1.5"
                />
              </div>
            </div>

            {/* Role dropdown */}
            <div>
              <Label htmlFor="coachRole" className="text-sm font-medium text-gray-700">
                Role <span className="text-red-500">*</span>
              </Label>
              <select
                id="coachRole"
                value={role}
                onChange={(e) => setRole(e.target.value as CoachRole)}
                className="mt-1.5 w-full rounded-lg border border-gray-300 bg-white px-3 py-2.5 text-sm min-h-[44px] focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
              >
                <option value="">Select role</option>
                <option value="Co-Coach">Co-Coach</option>
                <option value="Assistant Coach">Assistant Coach</option>
              </select>
            </div>

            {/* Invite toggle */}
            <div className="bg-blue-50 border border-blue-100 rounded-lg p-4">
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={wantsToInvite}
                  onChange={(e) => setWantsToInvite(e.target.checked)}
                  className="mt-0.5 w-4 h-4 text-green-600 border-gray-300 rounded focus:ring-green-500"
                />
                <div>
                  <p className="text-sm font-medium text-blue-900">Invite as collaborator</p>
                  <p className="text-xs text-blue-700 mt-0.5">
                    Send an email invitation so they can log in and view your team's players and assessments.
                  </p>
                </div>
              </label>
            </div>

            {/* Email field (shown when invite is checked) */}
            {wantsToInvite && (
              <div>
                <Label htmlFor="coachEmail" className="text-sm font-medium text-gray-700">
                  Email Address <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="coachEmail"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="coach@example.com"
                  className="mt-1.5"
                />
                {(hasExistingCollaborator || hasPendingInvitation) && (
                  <p className="text-xs text-amber-600 mt-1.5">
                    {hasExistingCollaborator
                      ? 'This team already has a collaborating coach. Remove them in Edit Team Settings first.'
                      : 'This team has a pending invitation. Cancel it in Edit Team Settings first.'}
                  </p>
                )}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-gray-100">
            <Button
              variant="outline"
              onClick={() => navigate('/dashboard')}
              className="min-h-[44px]"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              disabled={saving || inviting}
              className="bg-green-600 hover:bg-green-700 text-white min-h-[44px]"
            >
              {saving || inviting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  {inviting ? 'Sending Invitation...' : 'Saving...'}
                </>
              ) : wantsToInvite ? (
                <>
                  <Send className="w-4 h-4 mr-2" />
                  Save & Send Invitation
                </>
              ) : (
                <>
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Save
                </>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
